/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { useState, type ReactNode } from "react";

import { useFlag } from "@superweb/flags";
import { useLocale } from "@superweb/intl";
import {
  createComboBoxState,
  createDateFieldState,
  createTextFieldState,
  ExperimentalForm,
  useTypo,
  useUiColors,
} from "@superweb/ui";

import { useAction } from "@fleet/api";

import { useLogAction } from "#internal/analytics";
import { Message, useMessage } from "#internal/intl";
import { SubmitBottomSheet, useOperationIdContext } from "#internal/ui";
import {
  useErrorMessages,
  useOnChangePartial,
  useOnShowErrors,
} from "#internal/utils";

import {
  DriverLicenseDetailsFields,
  type DriverLicenseDetailsState,
} from "./driver-license-details-fields";

export const DriverLicenseDetailForm = ({
  countryCode,
  countryId,
  refId,
  bottomSheetContent,
  onSumbit,
  onError,
  onSuccess,
  licenseTemplatesOld,
}: {
  countryCode: string;
  countryId: string;
  refId: string;
  bottomSheetContent?: ReactNode;
  onSumbit: () => void;
  onError: (code?: string) => void;
  onSuccess: () => void;
  licenseTemplatesOld?: string[];
}) => {
  const typo = useTypo();
  const uiColors = useUiColors();
  const message = useMessage();
  const logActionSubmit = useLogAction("submit");
  const logActionInvalid = useLogAction("invalid");
  const locale = useLocale();
  const errorMessages = useErrorMessages();
  const flag = useFlag();

  const enableCountrySelection = flag({
    id: "72a2ff75-7bc1-467b-b095-6a90152b36d5",
    description: "Enable driver license country selection",
  });

  const { submissionId } = useOperationIdContext();

  const [formState, setFormState] = useState<DriverLicenseDetailsState>(() => {
    return {
      country: createComboBoxState(
        enableCountrySelection
          ? {
              value: undefined,
              errorMessage: errorMessages.requiredField,
            }
          : undefined,
      ),
      number: createTextFieldState({
        value: "",
        errorMessage: errorMessages.requiredField,
      }),
      issue_date: createDateFieldState({
        errorMessage: errorMessages.requiredField,
      }),
      expiry_date: createDateFieldState({
        errorMessage: errorMessages.requiredField,
      }),
    };
  });

  const { action: actionSubmit, isFetching } = useAction({
    url: "/fleet-forms/selfreg/v1/submit",
    method: "POST",
  });

  const onChangePartial = useOnChangePartial(setFormState);
  const onShowErrors = useOnShowErrors(setFormState);

  const [licenseTemplates, setLicenseTemplates] = useState<string[]>(
    licenseTemplatesOld ?? [],
  );

  const { action: getlicenseTemplatesData } = useAction({
    url: "/fleet-forms/selfreg/v1/license/number-templates",
    method: "GET",
  });

  const enableNewLicenseTemplates = flag({
    id: "561f48e6-9180-4133-9329-7adf38f05fd5",
    description: "Enable new driver license templates",
  });

  return (
    <ExperimentalForm
      main={
        <>
          <div
            css={{
              ...typo({
                level: "body2",
                density: "tight",
                weight: "regular",
              }),
              color: uiColors.textMinor,
            }}
          >
            <Message
              id="be435ebd-1277-4542-8ff7-81f87a331289"
              context="Yango forms. Driver details form. Step description"
              default="Add details to start earning right now"
            />
          </div>
          <DriverLicenseDetailsFields
            state={formState}
            onChangePartial={onChangePartial}
            countryCode={countryCode}
            countryId={countryId}
            licenseTemplates={licenseTemplates}
          />
          <SubmitBottomSheet
            isLoading={isFetching}
            text={message({
              id: "ac83bdb3-d54b-4579-a672-e1f688919efe",
              context: "Yango forms. Add info button text",
              default: "Add details",
            })}
          >
            {bottomSheetContent}
          </SubmitBottomSheet>
        </>
      }
      onSubmit={() => {
        onShowErrors();
        const hasErrors = Object.keys(formState).some(
          (field) =>
            formState[field as keyof DriverLicenseDetailsState].errorMessage !==
            undefined,
        );

        if (hasErrors) {
          if (
            (licenseTemplates.length > 0 &&
              formState.number.errorMessage ===
                errorMessages.invalidDriverLicenseWithTemplates(
                  licenseTemplates.join(", "),
                )) ||
            formState.number.errorMessage === errorMessages.invalidDriverLicense
          ) {
            logActionInvalid("invalid_driver_license", {
              onlyYM: true,
            });
          }

          if (
            formState.expiry_date.errorMessage === errorMessages.invalidDate
          ) {
            logActionInvalid("invalid_expiry_date", {
              onlyYM: true,
            });
          }

          if (formState.issue_date.errorMessage === errorMessages.invalidDate) {
            logActionInvalid("invalid_issue_date", {
              onlyYM: true,
            });
          }

          return;
        }

        actionSubmit(
          {
            headers: {
              "x-submission-id": submissionId,
              language: locale.toString(),
              "content-type": "application/json",
            },
            query: { ref_id: refId },
            body: {
              license: {
                number: formState.number.value,
                issue_date: String(formState.issue_date.value?.toString()),
                expiry_date: String(formState.expiry_date.value?.toString()),
                country_id: formState.country.value?.key ?? countryId,
              },
            },
          },
          {
            onResponse: (res) => {
              if (res.status === 204) {
                onSuccess();
                onSumbit();
                logActionSubmit("submit_license");
                return;
              }

              switch (res.body.code) {
                case "invalid_driver_license": {
                  logActionInvalid("invalid_driver_license", {
                    onlyYM: true,
                  });
                  if (enableNewLicenseTemplates) {
                    getlicenseTemplatesData(
                      {
                        headers: { language: locale.toString() },
                        query: {
                          country_id: formState.country.value?.key ?? countryId,
                        },
                      },
                      {
                        onResponse: (res) => {
                          let templates: string[] = [];
                          if (res.status === 200) {
                            setLicenseTemplates(
                              res.body.license_number_templates,
                            );
                            templates = res.body.license_number_templates;
                          }
                          onChangePartial({
                            number: {
                              ...formState.number,
                              errorMessage:
                                templates.length > 0
                                  ? errorMessages.invalidDriverLicenseWithTemplates(
                                      templates.join(", "),
                                    )
                                  : errorMessages.invalidDriverLicense,
                              errorVisible: true,
                            },
                          });
                        },
                      },
                    );
                  } else {
                    onChangePartial({
                      number: {
                        ...formState.number,
                        errorMessage:
                          licenseTemplates.length > 0
                            ? errorMessages.invalidDriverLicenseWithTemplates(
                                licenseTemplates.join(", "),
                              )
                            : errorMessages.invalidDriverLicense,
                        errorVisible: true,
                      },
                    });
                  }
                  break;
                }
                case "invalid_issue_date": {
                  logActionInvalid("invalid_issue_date", { onlyYM: true });
                  onChangePartial({
                    issue_date: {
                      ...formState.issue_date,
                      errorMessage: errorMessages.invalidDate,
                      errorVisible: true,
                    },
                  });
                  break;
                }
                case "invalid_expiry_date": {
                  logActionInvalid("invalid_expiry_date", {
                    onlyYM: true,
                  });
                  onChangePartial({
                    expiry_date: {
                      ...formState.expiry_date,
                      errorMessage: errorMessages.invalidDate,
                      errorVisible: true,
                    },
                  });
                  break;
                }
                default: {
                  onError(res.body.code);
                  break;
                }
              }
            },
          },
        );
      }}
    />
  );
};
